import React, { useState, useEffect } from 'react';
import { channelRegistrationService } from '../../services/api';
import { RefreshCw, Trash2, Eye, ArrowLeft, Plus, GripVertical } from 'lucide-react';

function ChannelManagement() {
  const [view, setView] = useState('list'); // 'list' | 'detail' | 'builder'
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReg, setSelectedReg] = useState(null);
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [msg, setMsg] = useState('');

  // Form builder state
  const [formConfig, setFormConfig] = useState({ title: '', description: '', fields: [], type_configs: {}, is_active: true });
  const [savingConfig, setSavingConfig] = useState(false);
  const [approving, setApproving] = useState(false);

  const loadRegistrations = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status = filter;
      if (typeFilter) params.type = typeFilter;
      if (search) params.search = search;
      const data = await channelRegistrationService.index(params);
      setRegistrations(data.data || data);
    } catch (err) {
      console.error('Error loading registrations:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFormConfig = async () => {
    try {
      const config = await channelRegistrationService.getConfig();
      setFormConfig({
        title: config.title || '',
        description: config.description || '',
        fields: config.fields || [],
        type_configs: config.type_configs || {},
        is_active: config.is_active ?? true,
      });
    } catch (err) {
      console.error('Error loading form config:', err);
    }
  };

  useEffect(() => {
    loadRegistrations();
  }, [filter, typeFilter, search]);

  useEffect(() => {
    if (view === 'builder' && !formConfig.fields.length) {
      loadFormConfig();
    }
  }, [view]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this registration?')) return;
    try {
      await channelRegistrationService.destroy(id);
      setRegistrations(registrations.filter(r => r.id !== id));
      setMsg('Registration deleted.');
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await channelRegistrationService.update(id, { status });
      setRegistrations(registrations.map(r => r.id === id ? { ...r, status } : r));
      if (selectedReg?.id === id) setSelectedReg({ ...selectedReg, status });
      setMsg(`Status updated to "${status}".`);
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleApprove = async (id) => {
    const reg = registrations.find(r => r.id === id) || selectedReg;
    const regType = (reg?.form_data?.registration_type || reg?.registration_type || 'dealer').toLowerCase();
    if (!window.confirm(`Approve this registration and create a ${regType.toUpperCase()} record? This will add them to the ${regType === 'dealer' ? 'Dealers' : 'Distributors'} section and they will appear on the website.`)) return;
    setApproving(true);
    try {
      const result = await channelRegistrationService.approve(id);
      setMsg(`✅ ${result.message}`);
      // Update local state
      setRegistrations(registrations.map(r => r.id === id ? { ...r, status: 'approved' } : r));
      if (selectedReg?.id === id) setSelectedReg({ ...selectedReg, status: 'approved' });
    } catch (err) {
      const apiMsg = err?.response?.data?.message || 'Failed to approve registration.';
      setMsg(`❌ ${apiMsg}`);
    } finally {
      setApproving(false);
    }
  };

  const openDetail = async (reg) => {
    try {
      const full = await channelRegistrationService.show(reg.id);
      setSelectedReg(full);
      setView('detail');
    } catch (err) {
      setSelectedReg(reg);
      setView('detail');
    }
  };

  const saveFormConfig = async () => {
    setSavingConfig(true);
    try {
      await channelRegistrationService.updateConfig(formConfig);
      setMsg('✅ Form configuration saved successfully!');
    } catch (err) {
      setMsg('Failed to save form configuration.');
    } finally {
      setSavingConfig(false);
    }
  };

  const addField = () => {
    setFormConfig({
      ...formConfig,
      fields: [...formConfig.fields, { name: '', label: '', type: 'text', required: false, width: 'full' }],
    });
  };

  const updateField = (idx, key, value) => {
    const fields = [...formConfig.fields];
    fields[idx] = { ...fields[idx], [key]: value };
    if (key === 'name' && !fields[idx].label) {
      fields[idx].label = value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
    }
    setFormConfig({ ...formConfig, fields });
  };

  const removeField = (idx) => {
    setFormConfig({ ...formConfig, fields: formConfig.fields.filter((_, i) => i !== idx) });
  };

  const moveField = (idx, dir) => {
    const fields = [...formConfig.fields];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= fields.length) return;
    [fields[idx], fields[newIdx]] = [fields[newIdx], fields[idx]];
    setFormConfig({ ...formConfig, fields });
  };

  const statusColors = {
    pending: 'bg-yellow-900/50 text-yellow-400',
    reviewed: 'bg-blue-900/50 text-blue-400',
    approved: 'bg-green-900/50 text-green-400',
    rejected: 'bg-red-900/50 text-red-400',
  };

  const TYPE_LABELS = {
    master_distributor: 'Master Distributor',
    super_distributor: 'Super Distributor',
    distributor: 'Distributor',
    retailer_dealer: 'Retailer / Dealer',
    franchise: 'Franchise',
    fofo_foco: 'FOFO & FOCO',
  };

  const ALL_TYPES = Object.keys(TYPE_LABELS);

  // ─── Form Builder View ──────────────────────────────────
  if (view === 'builder') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setView('list')} className="text-slate-400 hover:text-slate-200 transition">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-slate-100">Form Builder</h2>
              <p className="text-sm text-slate-400 mt-0.5">Configure the channel registration form fields</p>
            </div>
          </div>
          <button
            onClick={saveFormConfig}
            disabled={savingConfig}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-lg text-sm font-semibold transition disabled:opacity-60"
          >
            {savingConfig ? 'Saving...' : 'Save Form'}
          </button>
        </div>

        {msg && <div className="bg-green-900/30 border border-green-700/50 text-green-400 px-4 py-3 rounded-lg text-sm">{msg}</div>}

        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Form Title</label>
            <input
              value={formConfig.title}
              onChange={(e) => setFormConfig({ ...formConfig, title: e.target.value })}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Form Description</label>
            <textarea
              rows={2}
              value={formConfig.description}
              onChange={(e) => setFormConfig({ ...formConfig, description: e.target.value })}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500 resize-none"
            />
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <div
              className={`relative w-10 h-5 rounded-full transition ${formConfig.is_active ? 'bg-green-500' : 'bg-slate-600'}`}
              onClick={() => setFormConfig({ ...formConfig, is_active: !formConfig.is_active })}
            >
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${formConfig.is_active ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </div>
            <span className="text-sm text-slate-300">{formConfig.is_active ? '🟢 Form Active' : '🔴 Form Inactive'}</span>
          </label>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="bg-slate-800 px-4 py-2.5 flex items-center justify-between">
            <span className="font-semibold text-sm text-slate-200">Form Fields ({formConfig.fields.length})</span>
            <button onClick={addField} className="flex items-center gap-1 text-xs bg-slate-700 hover:bg-slate-600 text-slate-200 px-3 py-1.5 rounded-lg transition">
              <Plus className="w-3 h-3" /> Add Field
            </button>
          </div>
          <div className="divide-y divide-slate-800">
            {formConfig.fields.map((field, idx) => (
              <div key={idx} className="p-4 flex items-start gap-3">
                <div className="flex flex-col gap-1 pt-1">
                  <button onClick={() => moveField(idx, -1)} className="text-slate-500 hover:text-slate-300 text-xs">↑</button>
                  <button onClick={() => moveField(idx, 1)} className="text-slate-500 hover:text-slate-300 text-xs">↓</button>
                </div>
                <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-2">
                  <input
                    placeholder="Field name (no spaces)"
                    value={field.name}
                    onChange={(e) => updateField(idx, 'name', e.target.value)}
                    className="bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500"
                  />
                  <input
                    placeholder="Label"
                    value={field.label}
                    onChange={(e) => updateField(idx, 'label', e.target.value)}
                    className="bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500"
                  />
                  <select
                    value={field.type}
                    onChange={(e) => updateField(idx, 'type', e.target.value)}
                    className="bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500"
                  >
                    <option value="text">Text</option>
                    <option value="email">Email</option>
                    <option value="tel">Phone</option>
                    <option value="number">Number</option>
                    <option value="textarea">Textarea</option>
                    <option value="select">Select</option>
                    <option value="date">Date</option>
                  </select>
                  <select
                    value={field.width || 'full'}
                    onChange={(e) => updateField(idx, 'width', e.target.value)}
                    className="bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500"
                  >
                    <option value="full">Full Width</option>
                    <option value="half">Half Width</option>
                    <option value="third">Third Width</option>
                  </select>
                </div>
                <div className="flex flex-col gap-2 items-center">
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!field.required}
                      onChange={(e) => updateField(idx, 'required', e.target.checked)}
                      className="w-3.5 h-3.5 accent-red-500"
                    />
                    <span className="text-[10px] text-slate-400">Req</span>
                  </label>
                  <button onClick={() => removeField(idx)} className="text-red-400 hover:text-red-300 text-xs">✕</button>
                </div>
                {field.type === 'select' && (
                  <div className="col-span-4 mt-1">
                    <input
                      placeholder="Options (comma-separated)"
                      value={(field.options || []).join(', ')}
                      onChange={(e) => updateField(idx, 'options', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                      className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500"
                    />
                  </div>
                )}
                <div className="col-span-4 mt-1">
                  <label className="text-[10px] text-slate-500 block mb-1">Show for types:</label>
                  <div className="flex flex-wrap gap-2">
                    {['all', ...ALL_TYPES].map(t => (
                      <label key={t} className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(field.types || ['all']).includes(t)}
                          onChange={(e) => {
                            const current = field.types || ['all'];
                            let newTypes;
                            if (e.target.checked) {
                              if (t === 'all') newTypes = ['all'];
                              else newTypes = [...current.filter(x => x !== 'all'), t];
                            } else {
                              newTypes = current.filter(x => x !== t);
                              if (newTypes.length === 0) newTypes = ['all'];
                            }
                            updateField(idx, 'types', newTypes);
                          }}
                          className="w-3 h-3 accent-red-500"
                        />
                        <span className="text-[10px] text-slate-400">{t === 'all' ? 'All' : TYPE_LABELS[t]}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            {formConfig.fields.length === 0 && (
              <div className="p-8 text-center text-slate-500 text-sm">No fields configured. Click "Add Field" to create form fields.</div>
            )}
          </div>
        </div>

        {/* Per-Type Configuration */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="bg-slate-800 px-4 py-2.5">
            <span className="font-semibold text-sm text-slate-200">Per-Type Configuration</span>
          </div>
          <div className="divide-y divide-slate-800">
            {ALL_TYPES.map(typeKey => {
              const tc = formConfig.type_configs?.[typeKey] || {};
              return (
                <div key={typeKey} className="p-4 space-y-2">
                  <label className="text-xs text-slate-400 block capitalize font-semibold">{typeKey.replace(/_/g, ' ')}</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <input
                      placeholder="Title"
                      value={tc.title || ''}
                      onChange={(e) => setFormConfig({
                        ...formConfig,
                        type_configs: {
                          ...formConfig.type_configs,
                          [typeKey]: { ...tc, title: e.target.value }
                        }
                      })}
                      className="bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500"
                    />
                    <input
                      placeholder="Icon (emoji)"
                      value={tc.icon || ''}
                      onChange={(e) => setFormConfig({
                        ...formConfig,
                        type_configs: {
                          ...formConfig.type_configs,
                          [typeKey]: { ...tc, icon: e.target.value }
                        }
                      })}
                      className="bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500"
                    />
                    <input
                      placeholder="Description"
                      value={tc.description || ''}
                      onChange={(e) => setFormConfig({
                        ...formConfig,
                        type_configs: {
                          ...formConfig.type_configs,
                          [typeKey]: { ...tc, description: e.target.value }
                        }
                      })}
                      className="bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ─── Detail View ──────────────────────────────────
  if (view === 'detail' && selectedReg) {
    const data = selectedReg.form_data || {};
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <button onClick={() => setView('list')} className="text-slate-400 hover:text-slate-200 transition">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2 className="text-2xl font-bold text-slate-100">Registration Details</h2>
        </div>

        {msg && <div className="bg-green-900/30 border border-green-700/50 text-green-400 px-4 py-3 rounded-lg text-sm">{msg}</div>}

        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="bg-slate-800 px-4 py-2.5 flex items-center justify-between">
            <span className="font-semibold text-sm text-slate-200">Submission #{selectedReg.id}</span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[selectedReg.status] || statusColors.pending}`}>
              {selectedReg.status}
            </span>
          </div>
          <div className="p-6 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(data).map(([key, value]) => (
                <div key={key} className={key === 'address' || key === 'existing_business' || key === 'message' ? 'md:col-span-2' : ''}>
                  <label className="text-xs text-slate-400 block mb-0.5">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</label>
                  <div className="text-sm text-slate-200 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2">
                    {value || <span className="text-slate-500 italic">Not provided</span>}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-slate-800 pt-4 mt-4">
              <label className="text-xs text-slate-400 block mb-2">Update Status</label>
              <div className="flex flex-wrap gap-2">
                {['pending', 'reviewed', 'approved', 'rejected'].map(s => (
                  <button
                    key={s}
                    onClick={() => handleStatusUpdate(selectedReg.id, s)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition ${selectedReg.status === s ? 'bg-red-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {selectedReg.status !== 'approved' && (
              <div className="border-t border-slate-800 pt-4">
                <label className="text-xs text-slate-400 block mb-2">Approve & Create Record</label>
                <button
                  onClick={() => handleApprove(selectedReg.id)}
                  disabled={approving}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition disabled:opacity-60"
                >
                  {approving ? 'Processing...' : `✓ Approve & Create ${((selectedReg.form_data?.registration_type || selectedReg.registration_type || 'dealer')).charAt(0).toUpperCase() + (selectedReg.form_data?.registration_type || selectedReg.registration_type || 'dealer').slice(1).toLowerCase()}`}
                </button>
                <p className="text-xs text-slate-500 mt-2">This will create a record in the {(selectedReg.form_data?.registration_type || selectedReg.registration_type || 'dealer').toLowerCase() === 'dealer' ? 'Dealers' : 'Distributors'} section and the partner will appear on the website.</p>
              </div>
            )}

            {selectedReg.status === 'approved' && (
              <div className="border-t border-slate-800 pt-4">
                <div className="bg-green-900/30 border border-green-700/50 text-green-400 px-4 py-2.5 rounded-lg text-sm flex items-center gap-2">
                  ✓ This registration has been approved and a {(selectedReg.form_data?.registration_type || selectedReg.registration_type || 'dealer').toLowerCase() === 'dealer' ? 'Dealer' : 'Distributor'} record has been created.
                </div>
              </div>
            )}

            <div className="text-xs text-slate-500 border-t border-slate-800 pt-3">
              <div>Submitted: {new Date(selectedReg.created_at).toLocaleString()}</div>
              <div>Type: <span className="capitalize">{TYPE_LABELS[selectedReg.registration_type] || selectedReg.registration_type}</span></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── List View ──────────────────────────────────
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-100">Channel Registrations</h2>
          <p className="text-sm text-slate-400 mt-0.5">Master Distributor, Super Distributor, Distributor, Retailer/Dealer, Franchise & FOFO/FOCO registrations</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setView('builder')}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition"
          >
            ⚙ Form Builder
          </button>
          <button
            onClick={loadRegistrations}
            className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm font-semibold transition"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {msg && <div className="bg-green-900/30 border border-green-700/50 text-green-400 px-4 py-3 rounded-lg text-sm">{msg}</div>}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <input
          placeholder="Search by name, email, phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[200px] bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500"
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500"
        >
          <option value="">All Types</option>
          <option value="master_distributor">Master Distributor</option>
          <option value="super_distributor">Super Distributor</option>
          <option value="distributor">Distributor</option>
          <option value="retailer_dealer">Retailer / Dealer</option>
          <option value="franchise">Franchise</option>
          <option value="fofo_foco">FOFO & FOCO</option>
        </select>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="reviewed">Reviewed</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading registrations...</div>
      ) : registrations.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-slate-800 rounded-xl text-center">
          <div className="text-5xl mb-4">📋</div>
          <p className="text-slate-300 font-medium text-lg mb-1">No registrations yet</p>
          <p className="text-slate-500 text-sm">Channel partner registrations will appear here.</p>
        </div>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="grid grid-cols-12 text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-2 border-b border-slate-800 bg-slate-950">
            <div className="col-span-3">Business Name</div>
            <div className="col-span-2">Contact Person</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {registrations.map((reg, idx) => {
            const data = reg.form_data || {};
            return (
              <div key={reg.id} className={`grid grid-cols-12 items-center px-4 py-3 hover:bg-slate-800/50 transition ${idx !== 0 ? 'border-t border-slate-800' : ''}`}>
                <div className="col-span-3">
                  <button onClick={() => openDetail(reg)} className="text-slate-200 font-medium hover:text-red-400 transition text-left">
                    {data.business_name || 'N/A'}
                  </button>
                  <div className="text-xs text-slate-500">{data.email || ''}</div>
                </div>
                <div className="col-span-2 text-sm text-slate-300">
                  {data.contact_person || 'N/A'}
                  <div className="text-xs text-slate-500">{data.phone || ''}</div>
                </div>
                <div className="col-span-2">
                  <span className="text-xs text-slate-300">{TYPE_LABELS[reg.registration_type] || reg.registration_type}</span>
                </div>
                <div className="col-span-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[reg.status] || statusColors.pending}`}>
                    {reg.status}
                  </span>
                </div>
                <div className="col-span-2 text-xs text-slate-400">
                  {new Date(reg.created_at).toLocaleDateString()}
                </div>
                <div className="col-span-1 flex justify-end gap-1">
                  {reg.status !== 'approved' && (
                    <button onClick={() => handleApprove(reg.id)} title="Approve & Create" className="text-slate-400 hover:text-green-400 transition text-sm px-1">
                      ✓
                    </button>
                  )}
                  <button onClick={() => openDetail(reg)} title="View details" className="text-slate-400 hover:text-blue-400 transition text-sm px-1">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(reg.id)} title="Delete" className="text-slate-400 hover:text-red-400 transition text-sm px-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ChannelManagement;
